import type {
  CustomThemeDecoration,
  CustomThemeDecorationSpec,
  DecorationMotion,
  DecorationSprite,
} from '../../../themes/types';

interface DecorationSectionProps {
  readonly value: CustomThemeDecoration;
  readonly onChange: (value: CustomThemeDecoration) => void;
}

const SPRITES: DecorationSprite[] = [
  'cloud',
  'star',
  'bubble',
  'leaf',
  'sparkle',
  'snow',
  'fish',
  'butterfly',
];

const SPRITE_EMOJI: Record<DecorationSprite, string> = {
  cloud: '☁️',
  star: '⭐',
  bubble: '🫧',
  leaf: '🍃',
  sparkle: '✨',
  snow: '❄️',
  fish: '🐟',
  butterfly: '🦋',
};

const MOTIONS: DecorationMotion[] = ['drift', 'fall', 'float-up', 'orbit', 'twinkle'];

const DEFAULT_SPEC: CustomThemeDecorationSpec = {
  kind: 'spec',
  sprites: ['star'],
  motion: 'twinkle',
  density: 'med',
  speed: 'med',
  pickMode: 'all',
};

export function DecorationSection({ value, onChange }: DecorationSectionProps) {
  const off = value.kind === 'off';
  const spec = off ? DEFAULT_SPEC : value;

  const toggleSprite = (sprite: DecorationSprite) => {
    const next = spec.sprites.includes(sprite)
      ? spec.sprites.filter((s) => s !== sprite)
      : [...spec.sprites, sprite];
    if (next.length === 0) return;
    onChange({ ...spec, sprites: next });
  };

  return (
    <fieldset className="theme-editor__section">
      <legend>Tausta-animaatio</legend>
      <label>
        <input
          type="checkbox"
          checked={off}
          onChange={(e) => onChange(e.target.checked ? { kind: 'off' } : spec)}
        />
        <span>Ei mitään</span>
      </label>
      {off ? null : (
        <>
          <div className="theme-editor__chips">
            {SPRITES.map((s) => (
              <button
                key={s}
                type="button"
                className={spec.sprites.includes(s) ? 'active' : ''}
                onClick={() => toggleSprite(s)}
                aria-pressed={spec.sprites.includes(s)}
              >
                {SPRITE_EMOJI[s]}
              </button>
            ))}
          </div>
          <label>
            <span>Liike</span>
            <select
              value={spec.motion}
              onChange={(e) => onChange({ ...spec, motion: e.target.value as DecorationMotion })}
            >
              {MOTIONS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Tiheys</span>
            <select
              value={spec.density}
              onChange={(e) =>
                onChange({ ...spec, density: e.target.value as 'low' | 'med' | 'high' })
              }
            >
              <option value="low">Vähän</option>
              <option value="med">Keskiverto</option>
              <option value="high">Paljon</option>
            </select>
          </label>
          <label>
            <span>Nopeus</span>
            <select
              value={spec.speed}
              onChange={(e) =>
                onChange({ ...spec, speed: e.target.value as 'slow' | 'med' | 'fast' })
              }
            >
              <option value="slow">Hidas</option>
              <option value="med">Keskiverto</option>
              <option value="fast">Nopea</option>
            </select>
          </label>
          {spec.sprites.length > 1 ? (
            <label>
              <span>Valintatapa</span>
              <select
                value={spec.pickMode}
                onChange={(e) =>
                  onChange({ ...spec, pickMode: e.target.value as 'all' | 'random' })
                }
              >
                <option value="all">Kaikki</option>
                <option value="random">Arpa</option>
              </select>
            </label>
          ) : null}
        </>
      )}
    </fieldset>
  );
}
